import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { formatShortcut } from '../../utils/shortcuts';

const TOUR_SEEN_KEY = 'ript-tour-seen';
const CARD_WIDTH = 300;
const GAP = 12;        // space between the highlighted control and the card
const EDGE = 12;       // minimum distance from the window edge
const SPOT_PAD = 4;    // spotlight padding around the highlighted control

// Vercel builds a preview of every pushed branch at its own address, which
// starts with empty browser storage — don't greet the owner on every preview.
function isPreviewDeployment() {
  const host = window.location.hostname;
  return host.endsWith('.vercel.app') && host !== 'ript-editor.vercel.app';
}

export function shouldAutoShowTour() {
  return localStorage.getItem(TOUR_SEEN_KEY) !== 'true' && !isPreviewDeployment();
}

export function markTourSeen() {
  localStorage.setItem(TOUR_SEEN_KEY, 'true');
}

// The tour describes audible mode (the default for new users); the only
// mode-dependent wording is the name of the line-type button.
function buildSteps({ isAudible, guideWidth }) {
  const lineButton = isAudible ? 'Transcript line' : 'Speech';
  return [
    {
      target: null,
      title: 'Welcome to Ript',
      body: 'Ript is an editor for Conversation Analysis transcripts. Here is a quick look at the five things worth knowing before you start.',
    },
    {
      target: '[data-tour="line-type"]',
      title: 'Mark your transcript lines',
      body: `Put the cursor on a line of talk and click ${lineButton} (or press ${formatShortcut(['Alt'], '1')}). Leave titles and notes unmarked — only marked lines get numbers.`,
    },
    {
      target: '[data-tour="line-numbers"]',
      title: 'Number the lines',
      body: 'Click here to number every marked line. The numbers are part of the text, so they carry over when you copy or export. Click again to remove them.',
    },
    {
      target: '[data-tour="symbols"]',
      title: 'Insert transcription symbols',
      body: 'Hover over this menu and click a symbol, such as a pitch arrow (↑ ↓) or the degree sign for quiet talk (°), to insert it at the cursor.',
    },
    {
      target: 'guide',
      title: 'Keep lines to page width',
      body: `The vertical guide line marks ${guideWidth} characters. Keep your lines to the left of it so the transcript fits the page in your paper.`,
    },
    {
      target: '[data-tour="menu-file"]',
      title: 'Save your work',
      body: 'File → Save keeps an editable .ript file — keep this to carry on working later. File → Export as Word creates a document for your paper.',
      footnote: 'More settings, including a multimodal transcription mode, are in the Options menu. You can replay this tour from there too.',
    },
  ];
}

// Screen rectangle of a step's target, or null to show a centred card.
function getTargetRect(target) {
  if (!target) return null;

  if (target === 'guide') {
    // The guide is a pseudo-element, so derive its position from the editor.
    const pm = document.querySelector('.ProseMirror.has-guide');
    const area = document.querySelector('.editor-content');
    if (!pm || !area) return null;
    const guideLeft = parseFloat(getComputedStyle(pm).getPropertyValue('--guide-left'));
    if (Number.isNaN(guideLeft)) return null;
    const pmRect = pm.getBoundingClientRect();
    const areaRect = area.getBoundingClientRect();
    const x = pmRect.left + guideLeft;
    // On narrow screens the guide can sit beyond the visible area
    if (x > areaRect.right || x > window.innerWidth) return null;
    return {
      top: areaRect.top + SPOT_PAD,
      left: x - 3,
      width: 8,
      height: Math.min(areaRect.height - 2 * SPOT_PAD, 260),
    };
  }

  const el = document.querySelector(target);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

// Place the card below the target (or above if there is no room); tall,
// narrow targets like the guide line get the card beside them instead.
function getCardPosition(rect, cardHeight) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_WIDTH, vw - 2 * EDGE);

  if (!rect) {
    return { top: Math.max(EDGE, (vh - cardHeight) / 2), left: (vw - width) / 2, width };
  }

  const clampLeft = (left) => Math.max(EDGE, Math.min(left, vw - width - EDGE));

  if (rect.height > 120) {
    const right = rect.left + rect.width + GAP;
    const left = right + width + EDGE <= vw ? right : rect.left - GAP - width;
    return { top: rect.top + 40, left: clampLeft(left), width };
  }

  const below = rect.top + rect.height + SPOT_PAD + GAP;
  const top = below + cardHeight + EDGE <= vh
    ? below
    : Math.max(EDGE, rect.top - SPOT_PAD - GAP - cardHeight);
  return { top, left: clampLeft(rect.left), width };
}

const WelcomeTour = ({ isAudible, guideWidth, onClose }) => {
  const [steps] = useState(() => buildSteps({ isAudible, guideWidth }));
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardHeight, setCardHeight] = useState(180);
  const cardRef = useRef(null);
  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  const measure = useCallback(() => {
    setRect(getTargetRect(step.target));
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [step.target]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const position = getCardPosition(rect, cardHeight);

  return (
    <div className="tour-layer" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      {rect ? (
        <div
          className="tour-spotlight"
          style={{
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + 2 * SPOT_PAD,
            height: rect.height + 2 * SPOT_PAD,
          }}
        />
      ) : (
        <div className="tour-backdrop" />
      )}

      <div className="tour-card" ref={cardRef} style={position}>
        <button className="tour-close" onClick={onClose} aria-label="Close tour" title="Close (Esc)">
          <X size={14} />
        </button>
        <h3 className="dialog-title" id="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>
        {step.footnote && <p className="tour-footnote">{step.footnote}</p>}
        <div className="tour-footer">
          <span className="tour-progress">
            {isFirst ? '' : `${index} of ${steps.length - 1}`}
          </span>
          <div className="dialog-actions">
            {isFirst ? (
              <button className="dialog-button" onClick={onClose}>Skip</button>
            ) : (
              <button className="dialog-button" onClick={() => setIndex(index - 1)}>Back</button>
            )}
            <button
              className="dialog-button dialog-button-primary"
              onClick={isLast ? onClose : () => setIndex(index + 1)}
              autoFocus
            >
              {isFirst ? 'Show me around' : isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
